import React, { useState } from 'react'
import {
  Box,
  Grid,
  IconButton,
  Typography,
  Card,
  CardMedia,
  CircularProgress,
  Tooltip,
} from '@mui/material'
import {
  CloudUpload,
  Delete,
  Add,
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { getAbsoluteImageUrl, validateImageFile } from '../utils/imageUtils'

interface MultiImageUploadProps {
  label?: string
  values: string[]
  onChange: (urls: string[]) => void
  onUpload?: (file: File) => Promise<{ url: string }>
  maxImages?: number
  maxSizeMB?: number
  disabled?: boolean
}

const MultiImageUpload: React.FC<MultiImageUploadProps> = ({
  label = 'Additional Images',
  values = [],
  onChange,
  onUpload,
  maxImages = 6,
  maxSizeMB = 5,
  disabled = false,
}) => {
  const { enqueueSnackbar } = useSnackbar()
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null)



  const handleFileSelect = async (file: File, index: number) => {
    const error = validateImageFile(file, maxSizeMB)
    if (error) {
      enqueueSnackbar(error, { variant: 'error' })
      return
    }

    if (onUpload) {
      setUploadingIndex(index)
      try {
        const result = await onUpload(file)
        const newValues = [...values]
        newValues[index] = result.url
        onChange(newValues)
        enqueueSnackbar('Image uploaded successfully!', { variant: 'success' })
      } catch (error: any) {
        console.error('Upload error:', error)
        enqueueSnackbar(
          error?.response?.data?.detail || 'Failed to upload image',
          { variant: 'error' }
        )
      } finally {
        setUploadingIndex(null)
      }
    } else {
      const newValues = [...values]
      newValues[index] = URL.createObjectURL(file)
      onChange(newValues)
    }
  }

  const handleRemove = (index: number) => {
    const newValues = values.filter((_, i) => i !== index)
    onChange(newValues)
  }

  const renderImageSlot = (index: number) => {
    const hasImage = values[index] && values[index].trim() !== ''
    const isUploading = uploadingIndex === index

    return (
      <Grid item xs={6} sm={4} md={3} key={index}>
        <Card
          sx={{
            position: 'relative',
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: hasImage ? 'transparent' : '#e0e0e0',
            boxShadow: hasImage ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
            transition: 'all 0.3s ease',
            '&:hover': {
              borderColor: 'primary.main',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            },
          }}
        >
          {hasImage ? (
            <>
              <CardMedia
                component="img"
                image={getAbsoluteImageUrl(values[index])}
                alt={`Image ${index + 1}`}
                sx={{
                  width: '100%',
                  height: 150,
                  objectFit: 'cover',
                  bgcolor: '#f5f5f5',
                }}
                onError={(e) => {
                  // Handle broken image
                  const target = e.target as HTMLImageElement
                  target.style.display = 'none'
                  console.error('Failed to load image:', values[index])
                }}
              />
              <IconButton
                size="small"
                color="error"
                onClick={() => handleRemove(index)}
                disabled={disabled || isUploading}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'rgba(255,255,255,0.9)',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,1)',
                  },
                }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </>
          ) : (
            <Box
              component="label"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: 150,
                cursor: disabled || isUploading ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.6 : 1,
                bgcolor: '#fafafa',
              }}
            >
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) {
                    handleFileSelect(file, index)
                  }
                  e.target.value = ''
                }}
                disabled={disabled || isUploading}
              />
              {isUploading ? (
                <CircularProgress size={32} />
              ) : (
                <>
                  <CloudUpload sx={{ fontSize: 32, color: 'grey.400', mb: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    Image {index + 1}
                  </Typography>
                </>
              )}
            </Box>
          )}
        </Card>
      </Grid>
    )
  }

  return (
    <Box>
      {label && (
        <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
          {label}
        </Typography>
      )}
      <Grid container spacing={2}>
        {Array.from({ length: maxImages }).map((_, index) => renderImageSlot(index))}
      </Grid>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
        Upload up to {maxImages} additional images. PNG, JPG, WebP up to {maxSizeMB}MB each.
      </Typography>
    </Box>
  )
}

export default MultiImageUpload
